<template>
  <div class="m-snakerflow-designer">
    <WorkLibraryPanel v-if="lf && !viewer" :lf="lf" />
    <div :id="id" class="lf-container" :class="{'with-panel': !viewer}"></div>
    <PropertySetting ref="propertySetting" :extendAttrConfig="extendAttrConfig" :extendPropertyKeys="extendPropertyKeys" :node="nodeClick" v-model="processForm" :lf="lf">
      <template v-slot:[key]="data" v-for="(item, key) in $scopedSlots">
        <slot :name="key" v-bind="data || {}"></slot>
      </template>
    </PropertySetting>
    <el-dialog
        custom-class="m-dialog-data-detail"
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        destroy-on-close
        width="60%"
        @opened="handleOpened"
        :append-to-body="true"
        :before-close="handleClose">
        <component :is="dialogType" :graphData="graphData" ref="dialogComponent"></component>
        <span slot="footer" class="dialog-footer" v-if="dialogType=='ImportData' || dialogType=='HighLightData'">
            <el-button @click="handleClose">取 消</el-button>
            <el-button type="primary" @click="handleSubmit">确 定</el-button>
            <el-dropdown placement="top" trigger="click" style="float: left;" v-if="localStorageLfData.length" @command="handleCommand">
              <el-button type="text">选择<i class="el-icon-arrow-down el-icon--right"></i></el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :key="item.value" v-for="item in localStorageLfData" :command="item.value">
                  {{item.label}}&nbsp;&nbsp;<i class="el-icon-remove" @click="handleRemove(item.value)"></i>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
        </span>
        <span slot="footer" class="dialog-footer" v-if="dialogType=='DataDetail'">
            <el-button type="primary" class="m-btn-copy" :data-clipboard-text="copyJsonContent">复制JSON</el-button>
            <el-button type="primary" class="m-btn-copy" :data-clipboard-text="copyXmlContent">复制XML</el-button>
        </span>
        </el-dialog>
  </div>
</template>

<script>
import LogicFlow from '@logicflow/core'
import Task from './task'
import Transition from './transition'
import Start from './start'
import End from './end'
import Decision from './decision'
import PropertySetting from '../PropertySetting/index.vue'
import DataDetail from './control/DataDetail.vue'
import ImportData from './control/ImportData.vue'
import HighLightData from './control/HighLightData.vue'
import WorkLibraryPanel from './WorkLibraryPanel.vue'

import { Snapshot, SelectionSelect, Menu, Control, Group } from '@logicflow/extension'
import '@logicflow/core/dist/style/index.css'
import '@logicflow/extension/lib/style/index.css'
import { snakerXml2LogicFlowJson, logicFlowJsonToSnakerXml } from './tool'
import Clipboard from 'clipboard'
import { Message } from 'element-ui'
export default {
  name: 'SnakerFlowDesigner',
  components: {
    PropertySetting,
    DataDetail,
    ImportData,
    HighLightData,
    WorkLibraryPanel
  },
  props: {
    id: {
      type: String,
      default () {
        return 'lf_' + Date.now()
      }
    },
    value: {
      type: [String, Object],
      default () {
        return undefined
      }
    },
    config: {
      type: Object,
      default () {
        return {
          grid: true
        }
      }
    },
    viewer: { // 预览模式
      type: Boolean,
      default: false
    },
    showDoc: { // 是否显示文档
      type: Boolean,
      default: false
    },
    highLight: { // 高亮数据
      type: Object,
      default () {
        return {
        }
      }
    },
    extendAttrConfig: { // 扩展属性配置
      type: Object
    },
    // 扩展的属性key
    extendPropertyKeys: {
      type: Array
    },
    // 禁止点击的节点
    disabledClickNodes: {
      type: Array
    }
  },
  data () {
    return {
      lf: null,
      nodeClick: null,
      dialogTitle: '',
      dialogVisible: false,
      dialogType: 'DataDetail',
      graphData: {},
      clipboard: null,
      processForm: {},
      refreshLfData: 1
    }
  },
  watch: {
    value: {
      handler (n) {
        if (n && this.lf) {
          // this.init()
          const value = this.tranData(n)
          this.initProcessForm(value)
          if (value.nodes) {
            this.lf.render(value)
          }
        }
      },
      deep: true
    },
    processForm: {
      handler (n) {
        if (n) {
          delete n.type
          this.$emit('input', n)
        }
      },
      deep: true
    },
    highLight: {
      handler (n) {
        this.setHighLight(n)
      },
      deep: true
    }
  },
  computed: {
    copyJsonContent () {
      if (this.dialogType === 'DataDetail' && this.lf) {
        return JSON.stringify({
          ...this.tranData(this.value),
          ...this.lf.getGraphData()
        }, null, 2)
      }
      return ''
    },
    copyXmlContent () {
      if (this.dialogType === 'DataDetail' && this.lf) {
        return logicFlowJsonToSnakerXml({
          ...this.tranData(this.value),
          ...this.lf.getGraphData()
        })
      }
      return ''
    },
    localStorageLfData () {
      const res = []
      if (this.dialogType === 'ImportData' && [1, 2].includes(this.refreshLfData)) {
        Object.keys(window.localStorage).forEach(key => {
          if (key.startsWith('LFDATA###')) {
            const arr = key.split('###')
            res.push({
              value: key,
              label: arr[2]
            })
          }
        })
      }
      return res
    }
  },
  mounted () {
    this.init()
    if (this.tranData(this.value)) {
      this.initProcessForm(this.tranData(this.value))
      this.lf.render(this.tranData(this.value))
      this.setHighLight(this.highLight)
    }
  },
  beforeDestroy() {
    // 清理事件监听
    const container = document.getElementById(this.id)
    if (container) {
      container.removeEventListener('dragover', this.handleDragOver)
      container.removeEventListener('drop', this.handleDrop)
    }
  },
  methods: {
    tranData (n) {
      if (typeof n === 'string') {
        return snakerXml2LogicFlowJson(n)
      }
      return n
    },
    /**
     * 初始化流程定义表单信息
     */
    initProcessForm (data) {
      this.$set(this.processForm, 'type', 'snaker:process')
      this.$set(this.processForm, 'name', data.name)
      this.$set(this.processForm, 'displayName', data.displayName)
      this.$set(this.processForm, 'expireTime', data.expireTime)
      this.$set(this.processForm, 'instanceUrl', data.instanceUrl)
      this.$set(this.processForm, 'instanceNoClass', data.instanceNoClass)
      this.$set(this.processForm, 'preInterceptors', data.preInterceptors)
      this.$set(this.processForm, 'postInterceptors', data.postInterceptors)
    },
    init () {
      // 画布配置
      LogicFlow.use(Snapshot)
      // 不使用 DndPanel，使用自定义拖拽面板
      // LogicFlow.use(DndPanel)
      LogicFlow.use(SelectionSelect)
      LogicFlow.use(Menu)
      LogicFlow.use(Control)
      LogicFlow.use(Group)
      const defaultConfig = {}
      if (this.viewer) {
        defaultConfig.adjustEdge = false
        defaultConfig.adjustNodePosition = false
        defaultConfig.stopScrollGraph = true
        // defaultConfig.stopMoveGraph = true
        defaultConfig.hoverOutline = false
        defaultConfig.hideAnchors = true
        defaultConfig.adjustNodePosition = false
        defaultConfig.stopMoveGraph = false
        defaultConfig.nodeTextEdit = false
        defaultConfig.edgeTextEdit = false
        defaultConfig.resizeNode = false
        defaultConfig.nodeSelectedOutline = false
      }
      const lf = new LogicFlow({ ...this.config, ...defaultConfig, container: document.getElementById(this.id) })
      this.lf = lf
      // 注册自定义元素
      this.lf.register(Task)
      this.lf.register(Transition)
      this.lf.register(Start)
      this.lf.register(End)
      this.lf.register(Decision)

      // 初始化操作栏
      this.initOp(this.lf)
      // 初始化事件
      this.initEvent(lf)
    },
    initOp (lf) {
      if (this.viewer) {
        // 预览模式时
        this.lf.extension.menu.setMenuConfig({
          nodeMenu: [],
          edgeMenu: []
        })

        return
      }
      
      // 不再使用默认的 dndPanel，改用自定义拖拽面板
      // 监听画布的拖拽事件
      const container = document.getElementById(this.id)
      if (container) {
        container.addEventListener('dragover', this.handleDragOver)
        container.addEventListener('drop', this.handleDrop)
      }
      
      // 设置右键菜单
      this.lf.extension.menu.setMenuConfig({
        nodeMenu: [
          {
            text: '删除',
            callback (node) {
            // node为该节点数据
              lf.deleteNode(node.id)
            }
          }
        ]
      })
      // 控制面板-清空画布
      this.lf.extension.control.addItem({
        iconClass: 'lf-control-clear',
        title: 'clear',
        text: '清空',
        onClick: (lf, ev) => {
          lf.clearData()
        }
      })
      // 控制面板-添加查看按钮
      this.lf.extension.control.addItem({
        iconClass: 'lf-control-see',
        title: '',
        text: '查看',
        onClick: (lf, ev) => {
          this.graphData = {
            ...this.tranData(this.value),
            ...lf.getGraphData()
          }
          this.$nextTick(() => {
            this.dialogTitle = '查看流程数据'
            this.dialogVisible = true
            this.dialogType = 'DataDetail'
          })
        }
      })

      // 控制面板-设置高亮数据
      this.lf.extension.control.addItem({
        iconClass: 'lf-control-setting',
        title: '',
        text: '设置高亮',
        onClick: (lf, ev) => {
          this.dialogTitle = '导入高亮数据(json)'
          this.dialogVisible = true
          this.dialogType = 'HighLightData'
        }
      })
      // 控制面板-清空画布
      this.lf.extension.control.addItem({
        iconClass: 'lf-control-save',
        title: '',
        text: '保存',
        onClick: (lf, ev) => {
          this.graphData = lf.getGraphData()
          this.$nextTick(() => {
            const res = {
              ...this.tranData(this.value),
              ...this.graphData
            }
            this.$emit('on-save', {
              json: res,
              xml: logicFlowJsonToSnakerXml(res)
            })
          })
        }
      })
    },
    initEvent (lf) {
      if (this.viewer) return
      const { eventCenter } = this.lf.graphModel
      eventCenter.on('node:click', (args) => {
        if ((this.disabledClickNodes || []).includes(args.data.type)) {
          return
        }
        this.nodeClick = args.data

        this.$nextTick(() => {
          this.$refs.propertySetting.show(args.data)
        })
      })
      eventCenter.on('edge:click', (args) => {
        this.nodeClick = args.data
        this.$nextTick(() => {
          this.$refs.propertySetting.show(args.data)
        })
      })
      eventCenter.on('edge:add', (args) => {
        // 修改边类型
        lf.changeEdgeType(args.data.id, 'snaker:transition')
      })
      eventCenter.on('blank:contextmenu', (args) => {
        this.nodeClick = {
          name: this.processForm.name,
          displayName: this.processForm.displayName,
          expireTime: this.processForm.expireTime,
          instanceUrl: this.processForm.instanceUrl,
          instanceNoClass: this.processForm.instanceNoClass,
          preInterceptors: this.processForm.preInterceptors,
          postInterceptors: this.processForm.postInterceptors,
          type: 'snaker:process'
        }
        this.$nextTick(() => {
          this.$refs.propertySetting.show()
        })
      })
    },
    handleOpened () {
      if (this.dialogType === 'DataDetail') {
        const copyEl = document.querySelectorAll('.m-btn-copy')
        this.clipboard = new Clipboard(copyEl, {
          container: document.querySelector('.m-dialog-data-detail')
        })
        this.clipboard.on('success', function (e) {
          Message.success('复制成功')
          e.clearSelection()
        })
        this.clipboard.on('error', function (e) {
          Message.error('复制失败')
        })
      }
    },
    handleClose () {
      this.dialogVisible = false
      this.$refs.dialogComponent.handleClose()
      if (this.dialogType === 'DataDetail' && this.clipboard) {
        this.clipboard.destroy()
      }
    },
    handleSubmit () {
      if (this.dialogType === 'ImportData') {
        this.lf.clearData()
        try {
          this.importJson(this.$refs.dialogComponent.graphJsonStr)
        } catch {
          this.importXml(this.$refs.dialogComponent.graphJsonStr)
        }
      } else if (this.dialogType === 'HighLightData') {
        this.setHighLight(JSON.parse(this.$refs.dialogComponent.highLightJsonStr))
      }
      this.dialogVisible = false
    },
    /**
     * 导入snakerxml
     */
    importXml (xml) {
      if (!xml) return
      const data = snakerXml2LogicFlowJson(xml)
      this.initProcessForm(data)
      this.lf.render(data)
    },
    /**
     * 导入jsonstr
     */
    importJson (jsonStr) {
      if (!jsonStr) return
      let data = jsonStr
      if (typeof jsonStr === 'string') {
        data = JSON.parse(jsonStr)
      }
      this.initProcessForm(data)
      this.lf.render(data)
    },
    /**
     * 设置高亮数据
     * @param data { "historyNodeNames": [], "historyEdgeNames": [], "activeNodeNames": []}
     */
    setHighLight (data) {
      if (!this.lf || !data) return
      // 设置历史节点
      if (data && data.historyNodeNames) {
        data.historyNodeNames.forEach(nodeName => {
          this.lf.setProperties(nodeName, { state: 'history' })
        })
      }
      // 设置当前节点
      if (data && data.activeNodeNames) {
        data.activeNodeNames.forEach(nodeName => {
          this.lf.setProperties(nodeName, { state: 'active' })
        })
      }
      // 设置历史边
      if (data && data.historyEdgeNames) {
        data.historyEdgeNames.forEach(edgeName => {
          const edgeModel = this.lf.getEdgeModelById(edgeName)
          if (edgeModel) {
            edgeModel.setProperties({ state: 'history' })
          }
        })
      }
    },
    handleCommand (key) {
      this.$refs.dialogComponent.graphJsonStr = window.localStorage.getItem(key)
    },
    handleRemove (key) {
      window.localStorage.removeItem(key)
      if (this.refreshLfData === 1) {
        this.refreshLfData = 2
      } else {
        this.refreshLfData = 1
      }
    },
    
    // 自定义拖拽处理
    handleDragOver(event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    
    handleDrop(event) {
      event.preventDefault()
      console.log('[SnakerFlow] Drop event triggered')
      console.log('[SnakerFlow] Client position:', event.clientX, event.clientY)
      
      const dragDataStr = event.dataTransfer.getData('application/json')
      if (!dragDataStr) {
        console.warn('[SnakerFlow] No drag data found')
        return
      }
      
      console.log('[SnakerFlow] Drag data:', dragDataStr)
      
      const dragData = JSON.parse(dragDataStr)
      
      // 获取画布坐标 - 使用更可靠的方法
      let point = this.lf.getPointByClient(event.clientX, event.clientY)
      console.log('[SnakerFlow] getPointByClient result:', point)
      
      // 如果坐标转换失败或返回异常值，使用备用方法
      if (!point || isNaN(point.x) || isNaN(point.y) || Math.abs(point.y) > 10000) {
        console.warn('[SnakerFlow] Point conversion failed or abnormal, using fallback')
        const container = document.getElementById(this.id)
        const rect = container.getBoundingClientRect()
        
        console.log('[SnakerFlow] Container rect:', rect)
        
        // 计算相对于容器的坐标
        const domX = event.clientX - rect.left
        const domY = event.clientY - rect.top
        
        console.log('[SnakerFlow] DOM position:', domX, domY)
        
        // 获取画布的变换信息
        const graphModel = this.lf.graphModel
        const transformModel = graphModel.transformModel
        
        console.log('[SnakerFlow] Transform:', {
          SCALE_X: transformModel.SCALE_X,
          SCALE_Y: transformModel.SCALE_Y,
          TRANSLATE_X: transformModel.TRANSLATE_X,
          TRANSLATE_Y: transformModel.TRANSLATE_Y
        })
        
        // 转换为画布坐标（考虑缩放和平移）
        point = {
          x: (domX - transformModel.TRANSLATE_X) / transformModel.SCALE_X,
          y: (domY - transformModel.TRANSLATE_Y) / transformModel.SCALE_Y
        }
        
        console.log('[SnakerFlow] Fallback calculated point:', point)
      }
      
      // 再次检查坐标是否合理
      if (Math.abs(point.x) > 10000 || Math.abs(point.y) > 10000) {
        console.error('[SnakerFlow] Abnormal coordinates detected, using default center position')
        // 使用画布中心作为默认位置
        const graphModel = this.lf.graphModel
        point = {
          x: 0,
          y: 0
        }
      }
      
      console.log('[SnakerFlow] Final drop point:', point)
      console.log('[SnakerFlow] Drag type:', dragData.type)
      
      if (dragData.type === 'basic') {
        // 拖拽基础节点
        console.log('[SnakerFlow] Creating basic node:', dragData.nodeType)
        this.createBasicNode(point, dragData.nodeType, dragData.label)
      } else if (dragData.type === 'single') {
        // 拖拽单个工作项
        console.log('[SnakerFlow] Creating single task node:', dragData.workItem.name)
        this.createTaskNode(point, dragData.workItem, dragData.subCategory, dragData.category)
      } else if (dragData.type === 'group') {
        // 拖拽整个子类
        console.log('[SnakerFlow] Creating task group:', dragData.subCategory.subName)
        this.createTaskGroup(point, dragData.subCategory, dragData.category)
      }
    },
    
    createBasicNode(point, nodeType, label) {
      const nodeId = `${nodeType.split(':')[1]}_${Date.now()}`
      console.log('[SnakerFlow] Creating basic node with ID:', nodeId)
      
      const nodeData = {
        id: nodeId,
        type: nodeType,
        x: point.x,
        y: point.y,
        text: label,
        properties: {
          name: nodeId,
          displayName: label
        }
      }
      
      // 如果是任务节点，添加必要的属性
      if (nodeType === 'snaker:task') {
        nodeData.properties.width = 120
        nodeData.properties.height = 80
        nodeData.properties.workTree = []
      }
      
      console.log('[SnakerFlow] Node data:', nodeData)
      this.lf.addNode(nodeData)
      console.log('[SnakerFlow] Node added successfully')
    },
    
    createTaskNode(point, workItem, subCategory, category) {
      const nodeId = `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      console.log('[SnakerFlow] Creating task node with ID:', nodeId)
      
      const nodeData = {
        id: nodeId,
        type: 'snaker:task',
        x: point.x,
        y: point.y,
        text: workItem.name,
        properties: {
          name: nodeId,
          displayName: workItem.name,
          width: 120,
          height: 80,
          workTree: [{
            code: workItem.code,
            name: workItem.name,
            role: workItem.role,
            dispatchable: workItem.dispatchable,
            timeTracking: workItem.timeTracking,
            acceptance: workItem.acceptance,
            categoryCode: category.categoryCode,
            categoryName: category.categoryName,
            subCode: subCategory.subCode,
            subName: subCategory.subName
          }]
        }
      }
      
      console.log('[SnakerFlow] Task node data:', nodeData)
      this.lf.addNode(nodeData)
      console.log('[SnakerFlow] Task node added successfully')
    },
    
    createTaskGroup(point, subCategory, category) {
      const workItems = subCategory.workItems
      const spacing = 150 // 节点间距
      const startX = point.x
      const startY = point.y
      const baseTime = Date.now()
      
      console.log('[SnakerFlow] Creating task group at:', startX, startY)
      console.log('[SnakerFlow] Number of items:', workItems.length)
      
      const workTree = workItems.map(item => ({
        code: item.code,
        name: item.name,
        role: item.role,
        dispatchable: item.dispatchable,
        timeTracking: item.timeTracking,
        acceptance: item.acceptance,
        categoryCode: category.categoryCode,
        categoryName: category.categoryName,
        subCode: subCategory.subCode,
        subName: subCategory.subName
      }))
      
      workItems.forEach((workItem, index) => {
        const nodeId = `task_${baseTime + index}_${Math.random().toString(36).substr(2, 9)}`
        const nodeY = startY + (index * spacing)
        
        console.log(`[SnakerFlow] Creating node ${index + 1}/${workItems.length} at Y:`, nodeY)
        
        const nodeData = {
          id: nodeId,
          type: 'snaker:task',
          x: startX,
          y: nodeY,
          text: workItem.name,
          properties: {
            name: nodeId,
            displayName: workItem.name,
            width: 120,
            height: 80,
            workTree: workTree
          }
        }
        
        console.log('[SnakerFlow] Node data:', nodeData)
        this.lf.addNode(nodeData)
      })
      
      console.log('[SnakerFlow] Task group created successfully')
    }
  }
}
</script>
<style scoped>
.m-snakerflow-designer {
  height: 100%;
  position: relative;
  display: flex;
}
.snaker-flow-root {
  width: 100%;
  height: 100%;
}

.lf-container {
  flex: 1;
  height: 100%;
}

.lf-container.with-panel {
  margin-left: 280px;
}
</style>
<style>
.lf-control-see {
    background-image:url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/PjwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+PHN2ZyB0PSIxNjQ1NjgyNDM0MzQxIiBjbGFzcz0iaWNvbiIgdmlld0JveD0iMCAwIDEwMjQgMTAyNCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHAtaWQ9IjEzNzgiIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCI+PGRlZnM+PHN0eWxlIHR5cGU9InRleHQvY3NzIj48L3N0eWxlPjwvZGVmcz48cGF0aCBkPSJNNTEyIDE3MS4yODM2OTJjLTI1NS4wNTQ3NjkgMC00ODUuOTI3Mzg1IDMxMi40MzgxNTQtNDk2Ljc5NzUzOCAzMjcuMzI1NTM4bDAgMC42MzAxNTRDMjYuMDcyNjE1IDUxNC4xMjY3NjkgMjU2Ljk0NTIzMSA4MjYuNDg2MTU0IDUxMiA4MjYuNDg2MTU0YzI1NS4wMTUzODUgMCA0ODUuODg4LTMxMi4zOTg3NjkgNDk2Ljc5NzUzOC0zMjcuMzI1NTM4bDAtMC42MzAxNTRDOTk3LjkyNzM4NSA0ODMuNjgyNDYyIDc2Ny4wMTUzODUgMTcxLjI4MzY5MiA1MTIgMTcxLjI4MzY5Mkw1MTIgMTcxLjI4MzY5MnpNNTEyIDI5MS4yMDk4NDZjMTE0LjU2OTg0NiAwIDIwNy43NTM4NDYgOTMuMTA1MjMxIDIwNy43NTM4NDYgMjA3LjY3NTA3N1M2MjYuNTY5ODQ2IDcwNi42Mzg3NjkgNTEyIDcwNi42Mzg3NjljLTExNC41MzA0NjIgMC0yMDcuNzUzODQ2LTkzLjE4NC0yMDcuNzUzODQ2LTIwNy43NTM4NDZTMzk3LjQ2OTUzOCAyOTEuMjA5ODQ2IDUxMiAyOTEuMjA5ODQ2TTUxMiAzOTUuMjI0NjE1Yy01Ny4yNjUyMzEgMC0xMDMuNjYwMzA4IDQ2LjQzNDQ2Mi0xMDMuNjYwMzA4IDEwMy42NjAzMDhzNDYuMzk1MDc3IDEwMy42NjAzMDggMTAzLjY2MDMwOCAxMDMuNjYwMzA4YzU3LjIyNTg0NiAwIDEwMy42MjA5MjMtNDYuNDM0NDYyIDEwMy42MjA5MjMtMTAzLjY2MDMwOFM1NjkuMjI1ODQ2IDM5NS4yMjQ2MTUgNTEyIDM5NS4yMjQ2MTV6IiBwLWlkPSIxMzc5Ij48L3BhdGg+PC9zdmc+');
}
.lf-control-import {
    background-image:url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/PjwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+PHN2ZyB0PSIxNjQ1NjgyODQ5ODQ3IiBjbGFzcz0iaWNvbiIgdmlld0JveD0iMCAwIDEwMjQgMTAyNCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHAtaWQ9IjIxODMiIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCI+PGRlZnM+PHN0eWxlIHR5cGU9InRleHQvY3NzIj48L3N0eWxlPjwvZGVmcz48cGF0aCBkPSJNODg4LjMgNzU3LjRoLTUzLjhjLTQuMiAwLTcuNyAzLjUtNy43IDcuN3Y2MS44SDE5Ny4xVjE5Ny4xaDYyOS44djYxLjhjMCA0LjIgMy41IDcuNyA3LjcgNy43aDUzLjhjNC4yIDAgNy43LTMuNCA3LjctNy43VjE1OC43YzAtMTctMTMuNy0zMC43LTMwLjctMzAuN0gxNTguN2MtMTcgMC0zMC43IDEzLjctMzAuNyAzMC43djcwNi42YzAgMTcgMTMuNyAzMC43IDMwLjcgMzAuN2g3MDYuNmMxNyAwIDMwLjctMTMuNyAzMC43LTMwLjdWNzY1LjFjMC00LjMtMy41LTcuNy03LjctNy43eiIgcC1pZD0iMjE4NCI+PC9wYXRoPjxwYXRoIGQ9Ik05MDIgNDc2SDU4OHYtNzZjMC02LjctNy44LTEwLjUtMTMtNi4zbC0xNDEuOSAxMTJjLTQuMSAzLjItNC4xIDkuNCAwIDEyLjZsMTQxLjkgMTEyYzUuMyA0LjIgMTMgMC40IDEzLTYuM3YtNzZoMzE0YzQuNCAwIDgtMy42IDgtOHYtNTZjMC00LjQtMy42LTgtOC04eiIgcC1pZD0iMjE4NSI+PC9wYXRoPjwvc3ZnPg==');
}
.lf-control-clear {
  background-image:url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/PjwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+PHN2ZyB0PSIxNjQ1Nzg5MTYyODczIiBjbGFzcz0iaWNvbiIgdmlld0JveD0iMCAwIDEwMjQgMTAyNCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHAtaWQ9IjIwNDYiIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCI+PGRlZnM+PHN0eWxlIHR5cGU9InRleHQvY3NzIj48L3N0eWxlPjwvZGVmcz48cGF0aCBkPSJNODk5LjEgODY5LjZsLTUzLTMwNS42SDg2NGMxNC40IDAgMjYtMTEuNiAyNi0yNlYzNDZjMC0xNC40LTExLjYtMjYtMjYtMjZINjE4VjEzOGMwLTE0LjQtMTEuNi0yNi0yNi0yNkg0MzJjLTE0LjQgMC0yNiAxMS42LTI2IDI2djE4MkgxNjBjLTE0LjQgMC0yNiAxMS42LTI2IDI2djE5MmMwIDE0LjQgMTEuNiAyNiAyNiAyNmgxNy45bC01MyAzMDUuNmMtMC4zIDEuNS0wLjQgMy0wLjQgNC40IDAgMTQuNCAxMS42IDI2IDI2IDI2aDcyM2MxLjUgMCAzLTAuMSA0LjQtMC40IDE0LjItMi40IDIzLjctMTUuOSAyMS4yLTMwek0yMDQgMzkwaDI3MlYxODJoNzJ2MjA4aDI3MnYxMDRIMjA0VjM5MHogbTQ2OCA0NDBWNjc0YzAtNC40LTMuNi04LTgtOGgtNDhjLTQuNCAwLTggMy42LTggOHYxNTZINDE2VjY3NGMwLTQuNC0zLjYtOC04LThoLTQ4Yy00LjQgMC04IDMuNi04IDh2MTU2SDIwMi44bDQ1LjEtMjYwSDc3Nmw0NS4xIDI2MEg2NzJ6IiBwLWlkPSIyMDQ3Ij48L3BhdGg+PC9zdmc+');
}
.lf-control-save {
  background-image:url('data:image/svg+xml;base64,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');
}
.lf-control-mayu {
  background-image:url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAAAXNSR0IArs4c6QAAAVtJREFUOE+tVUFywjAMlKDPIINp+gnf4CUlLyF9SfuT0pM/0ZR0wjNg1JEdGzuRDYf6iOLV7motEArHKKVs+bJ4BcRfIOz1+ftYuoNS0axetoB0AICtUO/tb4SNBD4DNKvnAyC2JRahRtTq889b/G0CaKr6M8OqhN/rodv4DwKgqep3ANhPbrK8kmf++w89dA3ftYCjZ8zOnz7nUdwwUUS4Y08dYFWfAMBN1Blui6FZTjASq/L3rHQPSDE778msUXlSDtBUNfvAnfw56qHbTWxwUZmfm6pR2UOAeujkvE5TQdSikLspwyQW2aG4QoPChO9KjjxOc7u8btC+1+uSp1zyMCYWGE+Hxtb4KcedLMNcZEKc5kRsuKVgB8BcSkZVnIzb8oiD7dgkS6G4ooT3nj49C+gkpF3LQfZVeTkEULdM/2d9+ZYPsM0uD/EFJMCXJwVICojWAIuve38BfxdTzW7ahNZAAAAAAElFTkSuQmCC');
}
.lf-control-join {
  width: 70px;
  height: 55px;
  background-repeat: no-repeat;
  background-size: 35px 30px;
  background-position: center;
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAAAXNSR0IArs4c6QAAAOVJREFUOE/lkkESgjAMRZPxIrhz1EPAUTiFZQWu0FPITeQQlnFnbxINU5iQmVKZYQc72v6X5P8grPzhyjzYKvBQU7JD+Cg/HSE03QWvS3zuPfTAUglT/keC/FVgq6Gs4bN3gU7eBUMRXTtrcK+Bpxs9ASDRBWdTPt6pRILqJ2yswVxCZUEJnQV60YM7Cfg4nLsBGt1DPxr7OfHKFxgLWYM966+RCaHSaZ9rSgmBfeTgsiG4IFAIWmswC4SSStikQx+A9IrH5BTH6joUvTIjMLDYQAR5V2CzeLGXCGJvoynHAPp+g8AvqvthFal5qd4AAAAASUVORK5CYII=');
}
.lf-control-fork {
  width: 70px;
  height: 55px;
  background-repeat: no-repeat;
  background-size: 35px 30px;
  background-position: center;
  background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/PjwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+PHN2ZyB0PSIxNjQ2NDc3ODk0MjQzIiBjbGFzcz0iaWNvbiIgdmlld0JveD0iMCAwIDEwMjQgMTAyNCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHAtaWQ9IjI3MDEiIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCI+PGRlZnM+PHN0eWxlIHR5cGU9InRleHQvY3NzIj48L3N0eWxlPjwvZGVmcz48cGF0aCBkPSJNNjk4LjQgNjI5LjI4TDgwNS4zMjggNzM2SDYwOS43NmMtNDYuOTQ0IDAtNzkuODI0LTIxLjA0LTEwNi42MjQtNjguOTEyLTIwLjE2LTM1Ljk4NC03My4zMjgtMTIyLjQ5Ni05OC4wOC0xNjIuNjg4IDI0Ljc1Mi00MC4xNzYgNzcuOTItMTI3LjA0IDk4LjA4LTE2My4wMDhDNTI5LjkyIDI5My41MzYgNTYyLjgxNiAyNzIgNjA5Ljc2IDI3MmgxOTUuODA4bC0xMDcuMTg0IDEwNy40NCA1Ni41NzYgNTYuNjg4IDIwMy42NDgtMjAzLjU4NEw3NTQuOTYgMjguOTZsLTU2LjU3NiA1Ni4zMkw4MDUuMzQ0IDE5Mkg2MDkuNzZjLTc2LjE0NCAwLTEzNS41MDQgMzcuMDI0LTE3Ni40MzIgMTEwLjA4LTE5LjQ3MiAzNC44LTczLjYxNiAxMjkuOTItOTcuNiAxNjEuOTJINDguMjA4bC0wLjE0NCA4MGgyODcuNjMyYzI0IDMyIDc4LjE0NCAxMjcuMTA0IDk3LjYzMiAxNjEuOTJDNDc0LjI0IDc3OC45NiA1MzMuNiA4MTYgNjA5Ljc2IDgxNmgxOTUuODA4bC0xMDcuMTg0IDEwNy40NCA1Ni41NzYgNTYuNjg4IDIwMy42NDgtMjAzLjU4NC0yMDMuNjQ4LTIwMy43MjgtNTYuNTc2IDU2LjQ4eiIgZmlsbD0iIzFCN0ZGRiIgcC1pZD0iMjcwMiI+PC9wYXRoPjwvc3ZnPg==');
}
.lf-control-setting {
  width: 70px;
  height: 55px;
  background-repeat: no-repeat;
  background-size: 35px 30px;
  background-position: center;
  background-image: url('data:image/svg+xml;base64,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');
}
</style>
